import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { contractsApi, departmentsApi, type Contract, type Department } from "@/lib/api";
import { statusColors, statusLabels, type ContractStatus } from "@/lib/constants";

function formatCurrency(value: string | null): string {
  if (!value) return "—";
  const n = parseFloat(value);
  return `R ${(n / 1_000_000).toFixed(1)}M`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

export default function ContractList() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<ContractStatus | "">("");
  const [sortField, setSortField] = useState("end_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const fetchContracts = useCallback(() => {
    setLoading(true);
    contractsApi.list(page, {
      search: search || undefined,
      status: statusFilter || undefined,
      department: deptFilter || undefined,
      sort: sortField,
      dir: sortDir,
    })
      .then((res) => {
        setContracts(res.data.data);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, deptFilter, sortField, sortDir]);

  useEffect(() => {
    departmentsApi.list(1).then((res) => setDepartments(res.data.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchContracts, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchContracts, search]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-accent-400 text-4xl mb-3"></i>
        <p className="text-foreground-600 text-sm mb-1">Failed to load contracts</p>
        <p className="text-foreground-400 text-xs">{error}</p>
      </div>
    );
  }

  const COLS = [
    { field: "vendor_name", label: "Supplier" },
    { field: "department", label: "Department" },
    { field: "contract_value", label: "Value", align: "right" as const },
    { field: "end_date", label: "End Date", align: "right" as const },
    { field: "status", label: "Status", align: "center" as const },
    { field: "responsible_officer", label: "Officer" },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-primary-600 text-xl md:text-2xl font-bold">Contracts</h1>
          <p className="text-foreground-500 text-sm mt-1">
            {total > 0 ? `${total} contract${total !== 1 ? "s" : ""} found` : "Search, filter, and manage all contracts"}
          </p>
        </div>
        <button
          onClick={() => navigate("/contracts/new")}
          className="h-10 px-4 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-add-line text-sm"></i>
          <span>Add Contract</span>
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={handleFilterChange(setSearch)}
              placeholder="Search supplier, department, contract number..."
              className="w-full h-9 pl-9 pr-3 border border-background-300 rounded-md text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
          <select
            value={deptFilter}
            onChange={handleFilterChange(setDeptFilter)}
            className="h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 cursor-pointer"
          >
            <option value="">All Departments</option>
            {departments.map((d) => (<option key={d.id} value={d.name}>{d.name}</option>))}
          </select>
          <select
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter as (v: string) => void)}
            className="h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending_renewal">Decision Pending</option>
            <option value="expired">Lapsed</option>
            <option value="terminated">Terminated</option>
          </select>
          {(search || deptFilter || statusFilter) && (
            <button
              onClick={() => { setSearch(""); setDeptFilter(""); setStatusFilter(""); setPage(1); }}
              className="h-9 px-3 border border-background-300 text-foreground-500 text-sm rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-sm mr-1"></i>Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100">
                {COLS.map((col) => (
                  <th
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    className={`text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer hover:text-foreground-700 select-none ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sortField === col.field && (
                        <i className={`text-[10px] ${sortDir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-foreground-400 text-sm">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>Loading contracts...
                  </td>
                </tr>
              ) : contracts.map((contract) => {
                const st = statusColors[contract.status as ContractStatus] ?? statusColors.active;
                return (
                  <tr
                    key={contract.id}
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    className="border-b border-background-100 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="text-foreground-800 text-sm font-medium">{contract.vendor_name}</p>
                      <p className="text-foreground-400 text-xs">{contract.contract_number}</p>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 text-sm">{contract.department}</td>
                    <td className="px-4 py-3 text-right text-foreground-800 text-sm font-medium">{formatCurrency(contract.contract_value)}</td>
                    <td className="px-4 py-3 text-right text-foreground-600 text-sm">{formatDate(contract.end_date)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                        {statusLabels[contract.status as ContractStatus] ?? contract.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 text-sm">{contract.responsible_officer ?? "—"}</td>
                  </tr>
                );
              })}
              {!loading && contracts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-foreground-400 text-sm">
                    {(search || deptFilter || statusFilter) ? "No contracts match your filters." : "No contracts yet. Add your first contract."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-background-200">
            <p className="text-foreground-500 text-xs">
              {total} contract{total !== 1 ? "s" : ""} · page {page} of {lastPage}
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                <i className="ri-arrow-left-s-line text-sm"></i>
              </button>
              {Array.from({ length: Math.min(lastPage, 7) }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-md text-sm font-medium cursor-pointer ${p === page ? "bg-primary-600 text-white" : "border border-background-300 text-foreground-600 hover:bg-background-100"}`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page === lastPage}
                className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
